import { getFilenames } from "@services/storage/filenames";
import { getElements } from "@services/storage/elements";
import { getSkinini } from "@services/storage/skinini";
import { incrementDownloadCounts } from "@services/api/element";
import { formatBytes } from "@utils/format";
import type { FilenamesStorage } from "@app-types/storage.types";
import type { SkininiSettings } from "@app-types/skinini.types";
import type { StoredElement } from "@app-types/element.types";

/**
 * R2 Zip Worker URL
 */
const WORKER_URL = "https://download.osuskinner.com/";

/**
 * Progress callback type
 */
export type ProgressCallback = (loaded: number, total: number) => void;

// Re-export formatBytes for convenience
export { formatBytes };

/**
 * Trigger browser download of a blob
 */
export function triggerDownload(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

/**
 * Download a zip file containing the specified R2 files or skinini
 */
export async function downloadZipFromR2(
  filenames: FilenamesStorage | null,
  zipName: string = "download.osk",
  onProgress?: ProgressCallback,
  skinini: SkininiSettings | null = null,
  single?: boolean,
): Promise<void> {
  try {
    const response = await fetch(WORKER_URL, {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify({
        filenames: filenames,
        zipName: zipName,
        skinini: skinini,
        single: single,
      }),
    });

    if (!response.ok) {
      const error = (await response.json()) as { error?: string };
      throw new Error(error.error ?? `HTTP ${response.status}`);
    }

    // Get total size for progress tracking
    const contentLength = response.headers.get("Content-Length");
    const total = contentLength ? parseInt(contentLength, 10) : 0;

    // If progress callback provided, track progress (even if total is unknown)
    if (onProgress && response.body) {
      const reader = response.body.getReader();
      const chunks: Uint8Array[] = [];
      let loaded = 0;

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        chunks.push(value);
        loaded += value.length;
        onProgress(loaded, total);
      }

      // Combine chunks into blob
      const blob = new Blob(chunks as BlobPart[], {
        type: "application/octet-stream",
      });
      triggerDownload(blob, zipName);
    } else {
      // Simple download without progress
      const blob = await response.blob();
      triggerDownload(blob, zipName);
    }
  } catch (error) {
    console.error("Zip download failed:", error);
    throw error;
  }
}

/**
 * Download skin using filenames from localStorage
 * Convenience function that automatically retrieves file mappings
 */
export async function downloadSkin(
  onProgress?: ProgressCallback,
): Promise<void> {
  const filenames = getFilenames();
  const skinini = getSkinini();

  const skinName = skinini.Name ?? "Unnamed Skin";
  const zipName = `${skinName}.osk`;

  // Check if there are any files to download
  if (!filenames || Object.keys(filenames).length === 0) {
    throw new Error("No elements selected. Please select some elements first.");
  }

  await downloadZipFromR2(filenames, zipName, onProgress, skinini);

  // Increment download counts for all elements
  const elements = getElements();
  const elementIds = Object.values(elements).map((el) => el.element_id);
  incrementDownloadCounts(elementIds);
}

/**
 * Download skin using provided data (for skin page remix downloads)
 * Unlike downloadSkin(), this accepts filenames and skinini directly
 */
export async function downloadSkinFromData(
  filenames: FilenamesStorage,
  skinini: SkininiSettings,
  onProgress?: ProgressCallback,
  elements?: Record<string, StoredElement>,
): Promise<void> {
  const skinName = skinini.Name ?? "Unnamed Skin";
  const zipName = `${skinName}.osk`;

  if (!filenames || Object.keys(filenames).length === 0) {
    throw new Error("No elements in this skin.");
  }

  await downloadZipFromR2(filenames, zipName, onProgress, skinini);

  // Increment download counts if elements provided
  if (elements) {
    const elementIds = Object.values(elements).map((el) => el.element_id);
    incrementDownloadCounts(elementIds);
  }
}

/**
 * Download skinini as a zip file
 */
export async function downloadSkinini(
  onProgress?: ProgressCallback,
): Promise<void> {
  const skinini = getSkinini();

  // Check if there are any settings to download
  if (!skinini || Object.keys(skinini).length === 0) {
    throw new Error("No skin.ini settings to download.");
  }

  return downloadZipFromR2(null, "skin.ini.zip", onProgress, skinini);
}
